import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap, DollarSign, CalendarDays, Clock, TrendingDown,
  ChevronRight, ChevronDown, ChevronUp, Camera, CheckCircle2,
  Lightbulb, X, Package,
} from 'lucide-react';
import { LABELS, formatPastDate } from '../../constants/dashboard';
import { formatPrice } from '../../utils/formatters';

/* ── Alert Row (link-based) ──────────────────────────────── */
const AlertRow = ({ icon: Icon, iconColor, iconBg, children, timeAgo, linkTo, linkLabel, accentColor = 'text-amber-400' }) => (
  <Link
    to={linkTo}
    className="flex items-center justify-between p-3 bg-dark-bg/50 rounded-lg hover:bg-dark-bg/80 transition-colors group"
  >
    <div className="flex items-center gap-3 min-w-0">
      <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${iconBg}`}>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
      <span className="text-sm font-medium text-primary truncate">{children}</span>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
      {timeAgo && (
        <span className="text-[11px] text-muted whitespace-nowrap">{timeAgo}</span>
      )}
      <span className={`text-xs font-medium ${accentColor} group-hover:opacity-80 flex items-center gap-0.5`}>
        {linkLabel} <ChevronRight className="h-3 w-3" />
      </span>
    </div>
  </Link>
);

/* ── Recommendation Row (dismissable) ────────────────────── */
const RecommendationRow = ({ icon: Icon, iconColor, iconBg, children, linkTo, linkLabel, dismissable, onDismiss }) => (
  <div className="flex items-center justify-between p-3 bg-dark-bg/50 rounded-lg group">
    <div className="flex items-center gap-3 min-w-0">
      <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${iconBg}`}>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </div>
      <span className="text-sm font-medium text-primary truncate">{children}</span>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
      {linkTo && (
        <Link
          to={linkTo}
          className="text-xs font-medium text-cyan-400 hover:text-cyan-300 flex items-center gap-0.5"
        >
          {linkLabel} <ChevronRight className="h-3 w-3" />
        </Link>
      )}
      {dismissable && onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 text-muted/60 hover:text-red-400 transition-colors p-0.5"
          title="Masquer"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  </div>
);

/* ── Helper ──────────────────────────────────────────────── */
const getDaysInStock = (createdAt) => {
  if (!createdAt) return 0;
  return Math.floor((new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24));
};

/* ── NotificationCenter ──────────────────────────────────── */
const NotificationCenter = ({ vehicles, offers, upcomingAppointments, recommendations, dismissedRecs, onDismissRec }) => {
  const [expanded, setExpanded] = useState(false);

  const { alerts, recs } = useMemo(() => {
    const alertsList = [];
    const recsList = [];

    // ── ALERTS ──────────────────────────────────────────

    // 1. Pending offers (priority 1)
    const pendingOffersList = (offers || []).filter(o => {
      const status = (o.latest_offer || o).status;
      return status === 'pending' || status === 'countered';
    });
    pendingOffersList.forEach(offer => {
      const vehicle = offer.vehicle || {};
      alertsList.push({
        id: `offer-${offer.id}`,
        icon: DollarSign,
        iconColor: 'text-green-400',
        iconBg: 'bg-green-500/10',
        content: `${LABELS.alertNewOffer} ${formatPrice(offer.amount)} ${LABELS.alertOfferOn} ${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
        timeAgo: formatPastDate(offer.created_at || offer.updated_at),
        linkTo: '/marketplace/offers',
        linkLabel: LABELS.viewOffers,
        priority: 1,
      });
    });

    // 2. Pending appointments (priority 2)
    const pendingApts = (upcomingAppointments || []).filter(a => a.status === 'pending');
    pendingApts.forEach(apt => {
      const vehicle = apt.vehicle || {};
      const buyer = apt.buyer || {};
      alertsList.push({
        id: `apt-${apt.id}`,
        icon: CalendarDays,
        iconColor: 'text-blue-400',
        iconBg: 'bg-blue-500/10',
        content: `${LABELS.alertAppointmentToConfirm} — ${buyer.display_name || buyer.name || ''} · ${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
        timeAgo: formatPastDate(apt.created_at),
        linkTo: '/marketplace/appointments',
        linkLabel: LABELS.viewAppointments,
        priority: 2,
      });
    });

    // 3. Dormant stock >45j — aggregated (priority 3)
    const dormantVehicles = (vehicles || []).filter(v => {
      const days = getDaysInStock(v.created_at);
      return days > 45 && (v.stock_status === 'available' || !v.stock_status);
    });
    if (dormantVehicles.length > 0) {
      alertsList.push({
        id: 'dormant-stock',
        icon: Package,
        iconColor: 'text-amber-400',
        iconBg: 'bg-amber-500/10',
        content: `${dormantVehicles.length} véhicule${dormantVehicles.length > 1 ? 's' : ''} ${LABELS.notifDormantStock}`,
        timeAgo: '',
        linkTo: '/stock?health=dormant',
        linkLabel: LABELS.view,
        priority: 3,
      });
    }

    alertsList.sort((a, b) => a.priority - b.priority);

    // ── RECOMMENDATIONS ─────────────────────────────────

    const dismissed = dismissedRecs || [];

    // 1. API recommendations (priority from API, default 5)
    (recommendations || [])
      .filter(r => !dismissed.includes(r.id))
      .forEach(rec => {
        recsList.push({
          id: `rec-${rec.id}`,
          recId: rec.id,
          icon: Lightbulb,
          iconColor: 'text-cyan-400',
          iconBg: 'bg-cyan-500/10',
          content: rec.message,
          dismissable: true,
          priority: rec.priority || 5,
        });
      });

    // 2. Stock to watch 21-45j — aggregated (priority 10)
    const toWatchVehicles = (vehicles || []).filter(v => {
      const days = getDaysInStock(v.created_at);
      return days >= 21 && days <= 45 && (v.stock_status === 'available' || !v.stock_status);
    });
    if (toWatchVehicles.length > 0 && !dismissed.includes('stock-to-watch')) {
      recsList.push({
        id: 'stock-to-watch',
        recId: 'stock-to-watch',
        icon: Clock,
        iconColor: 'text-amber-400',
        iconBg: 'bg-amber-500/10',
        content: `${toWatchVehicles.length} véhicule${toWatchVehicles.length > 1 ? 's' : ''} ${LABELS.notifToWatch}`,
        linkTo: '/stock?health=toWatch',
        linkLabel: LABELS.view,
        dismissable: true,
        priority: 10,
      });
    }

    // 3. Missing photos — aggregated (priority 11)
    const noPhotos = (vehicles || []).filter(v =>
      (!v.images || v.images.length === 0) && v.stock_status !== 'sold'
    );
    if (noPhotos.length > 0 && !dismissed.includes('no-photos')) {
      recsList.push({
        id: 'no-photos',
        recId: 'no-photos',
        icon: Camera,
        iconColor: 'text-purple-400',
        iconBg: 'bg-purple-500/10',
        content: `${noPhotos.length} véhicule${noPhotos.length > 1 ? 's' : ''} ${LABELS.notifMissingPhotos}`,
        linkTo: '/stock?health=noPhotos',
        linkLabel: LABELS.view,
        dismissable: true,
        priority: 11,
      });
    }

    // 4. Overpriced >5% — aggregated (priority 12)
    const overpriced = (vehicles || []).filter(v => {
      if (!v.market_price || !v.price) return false;
      const ecart = ((v.price - v.market_price) / v.market_price) * 100;
      return ecart > 5 && (v.stock_status === 'available' || !v.stock_status);
    });
    if (overpriced.length > 0 && !dismissed.includes('overpriced')) {
      recsList.push({
        id: 'overpriced',
        recId: 'overpriced',
        icon: TrendingDown,
        iconColor: 'text-orange-400',
        iconBg: 'bg-orange-500/10',
        content: `${overpriced.length} véhicule${overpriced.length > 1 ? 's' : ''} ${LABELS.notifOverpriced}`,
        linkTo: '/stock?health=overpriced',
        linkLabel: LABELS.view,
        dismissable: true,
        priority: 12,
      });
    }

    recsList.sort((a, b) => a.priority - b.priority);

    return { alerts: alertsList, recs: recsList };
  }, [vehicles, offers, upcomingAppointments, recommendations, dismissedRecs]);

  const totalCount = alerts.length + recs.length;

  // ── State 1: All clear — nothing to show ────────────────
  if (totalCount === 0) {
    return null;
  }

  // ── Dynamic colors ──────────────────────────────────────
  const hasAlerts = alerts.length > 0;
  const borderColor = hasAlerts ? 'border-amber-500/20' : 'border-cyan-500/20';
  const bgColor = hasAlerts ? 'bg-amber-500/[0.06]' : 'bg-cyan-500/[0.06]';
  const headerIconColor = hasAlerts ? 'text-amber-400' : 'text-cyan-400';
  const headerIconBg = hasAlerts ? 'bg-amber-500/15' : 'bg-cyan-500/15';

  // Most urgent item for collapsed view
  const urgentItem = alerts[0] || recs[0];

  // Build summary text
  const summaryParts = [];
  if (alerts.length > 0) summaryParts.push(`${alerts.length} ${alerts.length > 1 ? 'alertes' : 'alerte'}`);
  if (recs.length > 0) summaryParts.push(`${recs.length} ${recs.length > 1 ? 'recommandations' : 'recommandation'}`);
  const summaryText = summaryParts.join(' · ');

  return (
    <div className={`${bgColor} rounded-xl border ${borderColor} p-4`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-2 mb-3"
      >
        <div className="flex items-center gap-2">
          <div className={`flex items-center justify-center w-6 h-6 rounded-md ${headerIconBg}`}>
            <Zap className={`h-3.5 w-3.5 ${headerIconColor}`} />
          </div>
          <span className={`text-sm font-semibold ${headerIconColor}`}>{summaryText}</span>
        </div>
        {expanded
          ? <ChevronUp className={`h-4 w-4 ${headerIconColor}`} />
          : <ChevronDown className={`h-4 w-4 ${headerIconColor}`} />
        }
      </button>

      {/* ── Collapsed: show most urgent item ──────────────── */}
      {!expanded && urgentItem && (
        <div className="space-y-2">
          {urgentItem.dismissable ? (
            <RecommendationRow
              icon={urgentItem.icon}
              iconColor={urgentItem.iconColor}
              iconBg={urgentItem.iconBg}
              linkTo={urgentItem.linkTo}
              linkLabel={urgentItem.linkLabel}
              dismissable={urgentItem.dismissable}
              onDismiss={() => onDismissRec?.(urgentItem.recId)}
            >
              {urgentItem.content}
            </RecommendationRow>
          ) : (
            <AlertRow
              icon={urgentItem.icon}
              iconColor={urgentItem.iconColor}
              iconBg={urgentItem.iconBg}
              timeAgo={urgentItem.timeAgo}
              linkTo={urgentItem.linkTo}
              linkLabel={urgentItem.linkLabel}
              accentColor={hasAlerts ? 'text-amber-400' : 'text-cyan-400'}
            >
              {urgentItem.content}
            </AlertRow>
          )}
        </div>
      )}

      {/* ── Expanded: all items grouped ───────────────────── */}
      {expanded && (
        <div className="space-y-4">
          {/* Alerts section */}
          {alerts.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-0.5 h-3.5 rounded-full bg-amber-400" />
                <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">
                  {LABELS.notifAlerts}
                </span>
              </div>
              <div className="space-y-2">
                {alerts.map(alert => (
                  <AlertRow
                    key={alert.id}
                    icon={alert.icon}
                    iconColor={alert.iconColor}
                    iconBg={alert.iconBg}
                    timeAgo={alert.timeAgo}
                    linkTo={alert.linkTo}
                    linkLabel={alert.linkLabel}
                    accentColor="text-amber-400"
                  >
                    {alert.content}
                  </AlertRow>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations section */}
          {recs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-0.5 h-3.5 rounded-full bg-cyan-400" />
                <span className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider">
                  {LABELS.notifRecommendations}
                </span>
              </div>
              <div className="space-y-2">
                {recs.map(rec => (
                  <RecommendationRow
                    key={rec.id}
                    icon={rec.icon}
                    iconColor={rec.iconColor}
                    iconBg={rec.iconBg}
                    linkTo={rec.linkTo}
                    linkLabel={rec.linkLabel}
                    dismissable={rec.dismissable}
                    onDismiss={rec.dismissable ? () => onDismissRec?.(rec.recId) : undefined}
                  >
                    {rec.content}
                  </RecommendationRow>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
